import {
	ReactElement,
	useCallback,
	useContext,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import {
	AppIcon,
	ChangeTriggerKind,
	SearchInputForm
} from "client/Components";
import { DestinationWindow } from "client/Api/Apps/common";
import { navigateTo } from "client_legacy/ndaw/navigator";
import styles from "./GoogleLiveTileContent.module.less";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { TileContext } from "../TileContext";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type GoogleLiveTileContentProps = {
	destinationWindow: DestinationWindow;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const GoogleLiveTileContent = (props: GoogleLiveTileContentProps): ReactElement => {
	const {
		destinationWindow,
		onOpenTileMenu
	} = props;

	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid()
	});

	const {
		icon,
		name,
		environmentAppId,
		dimension,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const submitHandler = useCallback(
		(value: string, version: Guid): void => {
			addAnalyticsEvent();

			setSearchTerm({
				value,
				version
			});

			const term: string = value.trim();

			if (!term) {
				return;
			}

			const url: string = "https://www.google.com/search?q=" + encodeURIComponent(term);

			if (destinationWindow === DestinationWindow.NewWindow) {
				navigateTo(url, true);
				setSearchTerm({
					value: "",
					version: newGuid()
				});
			} else {
				navigateTo(url);
			}
		},
		[
			destinationWindow,
			addAnalyticsEvent
		]
	);

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<div
				className={styles.container}
				title={name}
			>
				<div className={styles.input}>
					<SearchInputForm
						value={searchTerm}
						onChange={submitHandler}
						trigger={{
							kind: ChangeTriggerKind.OnEnter
						}}
						height="fill"
						variant="white"
					/>
				</div>
				<div className={styles.icon}>
					<AppIcon value={icon} color="inherit" />
				</div>
			</div>
		</LiveTileContent>
	);
};
GoogleLiveTileContent.displayName = "GoogleLiveTileContent";
